import { useState, useCallback, useRef } from 'react'

export interface PlaceSuggestion {
  placeId: string
  displayText: string
  prediction: google.maps.places.PlacePrediction
}

// Module-level session token — shared across hook instances; one token per autocomplete session.
// Reset to null after fetchFields() completes (ends the billing session).
let _sessionToken: google.maps.places.AutocompleteSessionToken | null = null

export function getOrCreateSessionToken(): google.maps.places.AutocompleteSessionToken {
  if (!_sessionToken) {
    _sessionToken = new google.maps.places.AutocompleteSessionToken()
  }
  return _sessionToken
}

export function resetSessionToken(): void {
  _sessionToken = null
}

interface UsePlaceAutocompleteReturn {
  /** Current list of autocomplete suggestions. Empty before first successful fetch. */
  suggestions: PlaceSuggestion[]
  /** True while a suggestions fetch is in flight. */
  loading: boolean
  /**
   * Trigger a debounced (200 ms) suggestions fetch for the given input text.
   * No-ops for inputs shorter than 2 characters.
   */
  fetchSuggestions: (input: string) => void
  /** Clear the current suggestions list (call on selection or input clear). */
  clearSuggestions: () => void
}

/**
 * Manages a Google Places autocomplete session.
 * Uses the Places API (New) — AutocompleteSuggestion.fetchAutocompleteSuggestions() —
 * which applies session-token billing (all keystrokes + the subsequent fetchFields call
 * are billed as a single session, not per-request).
 */
export function usePlaceAutocomplete(): UsePlaceAutocompleteReturn {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchSuggestions = useCallback((input: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!input.trim() || input.length < 2) {
      setSuggestions([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const token = getOrCreateSessionToken()
        const { suggestions: raw } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: token,
          })

        const mapped: PlaceSuggestion[] = raw
          .filter((s) => s.placePrediction != null)
          .map((s) => ({
            placeId: s.placePrediction!.placeId,
            displayText: s.placePrediction!.text.toString(),
            prediction: s.placePrediction!,
          }))

        setSuggestions(mapped)
      } catch (err) {
        // Log in dev so API errors (missing library, quota, key restrictions) are visible
        if (import.meta.env.DEV) {
          console.error('[usePlaceAutocomplete] fetchAutocompleteSuggestions failed:', err)
        }
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 200)
  }, [])

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  return { suggestions, loading, fetchSuggestions, clearSuggestions }
}
